import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Footprints, Zap, Target, Trophy, type LucideIcon } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import Sparkline from './Sparkline';
import { msToKmh } from '@/lib/utils';
import type { PerSessionEntry } from '@/types/playerCrossStats';

interface CrossKpiTilesProps {
  totalDistanceM: number;
  totalShots: number | null;
  sessionsCount: number;
  avgSpeedMs: number;
  maxSpeedMs: number;
  avgDistancePerSessionM: number;
  avgShotsPerSession: number | null;
  /** Chronologically sorted, dated sessions — used for sparklines and date range. */
  perSession: PerSessionEntry[];
  delay?: number;
}

const CrossKpiTiles: React.FC<CrossKpiTilesProps> = ({
  totalDistanceM,
  totalShots,
  sessionsCount,
  avgSpeedMs,
  maxSpeedMs,
  avgDistancePerSessionM,
  avgShotsPerSession,
  perSession,
  delay = 0,
}) => {
  const { t, i18n } = useTranslation(['report']);

  const dateRangeLabel = useMemo(() => {
    const dated = perSession.filter((s) => s.played_at);
    if (dated.length === 0) return null;
    const fmt = new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
    });
    const first = new Date(dated[0].played_at!);
    const last = new Date(dated[dated.length - 1].played_at!);
    if (dated.length === 1) return fmt.format(first);
    return `${fmt.format(first)} – ${fmt.format(last)}`;
  }, [perSession, i18n.language]);

  const distanceSeries = useMemo(
    () => perSession.map((s) => s.distance_m),
    [perSession],
  );
  const speedSeries = useMemo(
    () => perSession.map((s) => msToKmh(s.mean_velocity_ms)),
    [perSession],
  );
  const shotsSeries = useMemo(
    () => perSession.map((s) => (typeof s.shots === 'number' ? s.shots : 0)),
    [perSession],
  );

  const distanceLabel = formatDistance(totalDistanceM);
  const avgDistanceLabel = formatDistance(avgDistancePerSessionM);
  const avgSpeedLabel = `${msToKmh(avgSpeedMs).toFixed(1)} km/h`;
  const maxSpeedLabel = `${msToKmh(maxSpeedMs).toFixed(1)} km/h`;

  return (
    <div
      className="grid grid-cols-2 gap-3 lg:grid-cols-4"
      style={{ animation: `fade-in 0.5s ease-out ${delay}ms both` }}
    >
      <KpiTile
        label={t('report:playerReport.totalDistance')}
        icon={Footprints}
        value={distanceLabel}
        sparkline={distanceSeries}
        subtitle={t('report:playerReport.kpi.avgPerSession', {
          value: avgDistanceLabel,
        })}
      />
      <KpiTile
        label={t('report:playerReport.sessionsPlayed')}
        icon={Trophy}
        value={String(sessionsCount)}
        subtitle={dateRangeLabel ?? undefined}
      />
      <KpiTile
        label={t('report:playerReport.avgSpeed')}
        icon={Zap}
        value={avgSpeedLabel}
        sparkline={speedSeries}
        subtitle={t('report:playerReport.kpi.max', { value: maxSpeedLabel })}
      />
      <KpiTile
        label={t('report:playerReport.totalShots')}
        icon={Target}
        value={totalShots != null ? String(totalShots) : '—'}
        sparkline={
          totalShots != null && shotsSeries.length > 0 ? shotsSeries : undefined
        }
        subtitle={
          avgShotsPerSession != null
            ? t('report:playerReport.kpi.avgPerSession', {
                value: Math.round(avgShotsPerSession),
              })
            : undefined
        }
      />
    </div>
  );
};

interface KpiTileProps {
  label: string;
  icon: LucideIcon;
  value: string;
  sparkline?: number[];
  subtitle?: string;
}

const KpiTile: React.FC<KpiTileProps> = ({
  label,
  icon: Icon,
  value,
  sparkline,
  subtitle,
}) => (
  <Card>
    <CardContent className="p-5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">
          {label}
        </span>
        <Icon className="h-4 w-4 shrink-0 text-slate-400" />
      </div>
      <div className="text-2xl font-bold tracking-tight text-slate-900">
        {value}
      </div>
      {sparkline && sparkline.length >= 2 && (
        <div className="mt-3">
          <Sparkline values={sparkline} height={28} />
        </div>
      )}
      {subtitle && (
        <p className="mt-2 text-xs leading-tight text-slate-500">{subtitle}</p>
      )}
    </CardContent>
  </Card>
);

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '0 m';
  return meters >= 1000
    ? `${(meters / 1000).toFixed(1)} km`
    : `${Math.round(meters)} m`;
}

export default CrossKpiTiles;
